import * as Inventory from '../inventory';
import * as Native from '.';
import createActions from '../config/actions/createActions';
import { action } from '@play-co/replicant';
import { initializeFirstAppInstall } from './native.modifiers';

export const nativeActions = createActions({
  /**
   * Update last launch date for IOS devices
   */
  updateAppleLastLaunched: action((state, _, api) => {
    // Is this first native app install for this user?
    if (state.native.appleLastLaunched === 0) {
      initializeFirstAppInstall(state);
    }

    if (state.native.appleLastLaunched) {
      state.native.lastNativeEntry = state.native.appleLastLaunched;
    }

    state.native.appleLastLaunched = api.date.now();
  }),

  /**
   * Update last launch date for ANDROID devices
   */
  updateGoogleLastLaunched: action((state, _, api) => {
    // Is this first native app install for this user?
    if (state.native.googleLastLaunched === 0) {
      initializeFirstAppInstall(state);
    }

    if (state.native.googleLastLaunched) {
      state.native.lastNativeEntry = state.native.googleLastLaunched;
    }

    state.native.googleLastLaunched = api.date.now();
  }),

  /**
   * Native app install reward promo was shown for this user
   */
  installRewardWasShown: action((state, _, api) => {
    if (state.native.appInstallRewardStatus === 'idle') {
      state.native.appInstallRewardStatus = 'saw_promo';
    }
  }),

  /**
   * Claim reward for installation native app
   */
  claimInstallReward: action((state, _, api) => {
    const reward = Native.ruleset.appInstallReward;

    // If reward state is in received give reward
    if (state.native.appInstallRewardStatus === 'received') {
      // Give reward
      Inventory.modifiers.addFreeGems(
        state,
        reward.gems,
        {
          feature: 'native_install',
        },
        api,
      );

      // Switch to claimed state
      state.native.appInstallRewardStatus = 'claimed';
    }
  }),

  /**
   * Incentivised app update popup was shown for this user
   */
  incentivisedUpdateWasShown: action((state) => {
    if (state.native.appUpdateRewardStatus === 'idle') {
      state.native.appUpdateRewardStatus = 'saw_promo';
    }
  }),

  /**
   * Mark user as eligible to receive the incentivised update reward
   * Called when user returns on an updated app version after seeing the promo
   */
  markIncentivisedUpdateRewardReceived: action((state) => {
    if (state.native.appUpdateRewardStatus === 'saw_promo') {
      state.native.appUpdateRewardStatus = 'received';
    }
  }),

  /**
   * Claim reward for updating native app (incentivised update)
   */
  claimIncentivisedUpdateReward: action((state, _, api) => {
    const reward = Native.ruleset.appUpdateReward;

    // If reward state is in received give reward
    if (state.native.appUpdateRewardStatus === 'received') {
      // Give reward
      Inventory.modifiers.addFreeGems(
        state,
        reward.gems,
        {
          feature: 'native_incentivised_update',
        },
        api,
      );

      // Switch to claimed state
      state.native.appUpdateRewardStatus = 'claimed';
    }
  }),

  // Will unset the token if the token is undefined
  setNativeDeviceToken: action(
    (state, args: { ios?: string; android?: string }, api) => {
      const { ios, android } = args;

      if ('ios' in args) {
        api.chatbot.setAppleDeviceToken(ios || undefined);
      }

      if ('android' in args) {
        api.chatbot.setAndroidDeviceToken(android || undefined);
      }

      // Save when we updated token
      state.native.pushNotificationTokenUpdatedAt = api.date.now();
    },
  ),

  isNativeDeviceTokenValid: action((_, platform: 'ios' | 'android', api) => {
    if (platform === 'ios') {
      return api.chatbot.appleDeviceTokenIsValid();
    }
    if (platform === 'android') {
      return api.chatbot.androidDeviceTokenIsValid();
    }

    return false;
  }),

  setPersistedPayload: action((state, payload?: string) => {
    if (!payload) {
      delete state.native.payload;
      return;
    }
    state.native.payload = payload;
  }),

  /**
   * Set flag that we are tracking this user native app re-installs
   * Only on native app, before we set this flag to true we also set local storage flag.
   * If user re-install app it will clear local storage, and we can detect possible re-install by checking this flag and local storage flag.
   *
   * @see User property `lastEntryIsFirstOfNativeInstall`
   */
  setFirstOfNativeInstallTrackedFlag: action((state, shouldTrack: boolean) => {
    state.native.isFirstOfNativeInstallTracked = shouldTrack;
  }),
});
